import { Component, OnInit, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { ControlInfoModel } from "@app/controls/models/ControlInfoModel";
import { CustomFieldHelper } from "@app/shared/helpers/customField-helper";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { Pages } from "@constants/pages/Pages";
import { FilterType } from "@enums/FilterType";
import { ModuleType } from "@enums/ModuleType";
import { StandingDataType } from "@enums/StandingDataType";
import { nameof } from "@methods/jeffs-toolkit";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { WorkspaceButtonConfig } from "@models/workspace/WorkspaceButtonConfig";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { TabService } from "@services/tabs/TabService";
import { ControlDataService } from "../services/control.data.service";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";

@Component({
    selector: "app-control-overview",
    templateUrl: "./control-overview.component.html",
    styleUrls: ["./control-overview.component.scss"],
    imports: [WorkspaceComponent],
})
export class ControlOverviewComponent implements OnInit {
    private _controlDS = inject(ControlDataService);
    private _tabs = inject(TabService);
    pages = inject(Pages);
    private _permissions = inject(PermissionsService);
    private standingDataService = inject(StandingdataDataService);
    private _translation = injectTranslation(scope);


    moduleType = ModuleType.Controls;
    tabID: string;
    tab: TabModel;
    idProp: string = nameof<ControlInfoModel>((x) => x.guid);
    data: Promise<ControlInfoModel[]>;
    headerLookup: { [propName: string]: string };

    private buildHeaderLookup(): { [propName: string]: string } {
        const t = (key: string) => this._translation.t(`overview.${key}`);
        const headerLookup: { [propName: string]: string } = {};
        headerLookup[nameof<ControlInfoModel>((x) => x.identifier)] = t("headerIdentifier");
        headerLookup[nameof<ControlInfoModel>((x) => x.name)] = t("headerName");
        headerLookup[nameof<ControlInfoModel>((x) => x.isKeyControl)] = t("headerIsKeyControl");
        headerLookup[nameof<ControlInfoModel>((x) => x.designScore)] = t("headerDesignScore");
        headerLookup[nameof<ControlInfoModel>((x) => x.implementationScore)] = t("headerImplementationScore");
        headerLookup[nameof<ControlInfoModel>((x) => x.diStatus)] = t("headerDiStatus");
        headerLookup[nameof<ControlInfoModel>((x) => x.diScoreComment)] = t("headerDiScoreComment");
        headerLookup[nameof<ControlInfoModel>((x) => x.effectivenessScore)] = t("headerEffectivenessScore");
        headerLookup[nameof<ControlInfoModel>((x) => x.effectivenessConclusionComment)] = t("headerEffectivenessConclusionComment");
        headerLookup[nameof<ControlInfoModel>((x) => x.effectivenessStatus)] = t("headerEffectivenessStatus");
        headerLookup[nameof<ControlInfoModel>((x) => x.finalConclusionScore)] = t("headerFinalConclusionScore");
        headerLookup[nameof<ControlInfoModel>((x) => x.latestPeriodScore)] = t("headerLatestPeriodScore");
        headerLookup[nameof<ControlInfoModel>((x) => x.controlObjective)] = t("headerControlObjective");
        headerLookup[nameof<ControlInfoModel>((x) => x.organization)] = t("headerOrganization");
        headerLookup[nameof<ControlInfoModel>((x) => x.dateEffTested)] = t("headerDateEffTested");
        headerLookup[nameof<ControlInfoModel>((x) => x.dateDiTested)] = t("headerDateDiTested");
        headerLookup[nameof<ControlInfoModel>((x) => x.isEffOverdue)] = t("headerIsEffOverdue");
        headerLookup[nameof<ControlInfoModel>((x) => x.isDiOverdue)] = t("headerIsDiOverdue");
        headerLookup[nameof<ControlInfoModel>((x) => x.diEvidenceUploader)] = t("headerDiEvidenceUploader");
        headerLookup[nameof<ControlInfoModel>((x) => x.diTester)] = t("headerDiTester");
        headerLookup[nameof<ControlInfoModel>((x) => x.effEvidenceUploader)] = t("headerEffEvidenceUploader");
        headerLookup[nameof<ControlInfoModel>((x) => x.effTester)] = t("headerEffTester");
        headerLookup[nameof<ControlInfoModel>((x) => x.controlCatalogue)] = t("headerControlCatalogue");
        headerLookup[nameof<ControlInfoModel>((x) => x.type)] = t("headerType");
        headerLookup[nameof<ControlInfoModel>((x) => x.frameworkDimensions)] = t("headerFrameworkDimensions");
        headerLookup[nameof<ControlInfoModel>((x) => x.businessDimensions)] = t("headerBusinessDimensions");
        headerLookup[nameof<ControlInfoModel>((x) => x.description)] = t("headerDescription");
        headerLookup[nameof<ControlInfoModel>((x) => x.controlExecutionMethod)] = t("headerControlExecutionMethod");
        headerLookup[nameof<ControlInfoModel>((x) => x.source)] = t("headerSource");
        headerLookup[nameof<ControlInfoModel>((x) => x.aspectIc)] = t("headerAspectIc");
        headerLookup[nameof<ControlInfoModel>((x) => x.owner)] = t("headerOwner");
        headerLookup[nameof<ControlInfoModel>((x) => x.controlFrequency)] = t("headerControlFrequency");
        headerLookup[nameof<ControlInfoModel>((x) => x.inPlace)] = t("headerInPlace");
        headerLookup[nameof<ControlInfoModel>((x) => x.requiresMonitoring)] = t("headerRequiresMonitoring");
        headerLookup[nameof<ControlInfoModel>((x) => x.isShared)] = t("headerIsShared");
        headerLookup[nameof<ControlInfoModel>((x) => x.startDate)] = t("headerStartDate");
        headerLookup[nameof<ControlInfoModel>((x) => x.endDate)] = t("headerEndDate");
        headerLookup[nameof<ControlInfoModel>((x) => x.comments)] = t("headerComments");
        headerLookup[nameof<ControlInfoModel>((x) => x.dateCreated)] = t("headerDateCreated");
        headerLookup[nameof<ControlInfoModel>((x) => x.dateModified)] = t("headerDateModified");
        headerLookup[nameof<ControlInfoModel>((x) => x.lastModifier)] = t("headerLastModifier");
        headerLookup[nameof<ControlInfoModel>((x) => x.nextEffTestDate)] = t("headerNextEffTestDate");
        headerLookup[nameof<ControlInfoModel>((x) => x.nextDiTestDate)] = t("headerNextDiTestDate");
        headerLookup[nameof<ControlInfoModel>((x) => x.requiresControlExecution)] = t("headerRequiresControlExecution");
        headerLookup[nameof<ControlInfoModel>((x) => x.lastControlExecutionDate)] = t("headerLastControlExecutionDate");
        headerLookup[nameof<ControlInfoModel>((x) => x.nextControlExecutionDate)] = t("headerNextControlExecutionDate");
        headerLookup[nameof<ControlInfoModel>((x) => x.moiCount)] = t("headerMoiCount");
        headerLookup[nameof<ControlInfoModel>((x) => x.riskCount)] = t("headerRiskCount");
        headerLookup[nameof<ControlInfoModel>((x) => x.linkedRiskNames)] = t("headerLinkedRiskNames");
        headerLookup[nameof<ControlInfoModel>((x) => x.organizationPath)] = t("headerOrganizationPath");
        return headerLookup;
    }

    filterConfigs: FilterConfig[];

    rendererConfig: RendererConfig[] = [
        RendererConfig.createScoreConfig(
            nameof<ControlInfoModel>((x) => x.designScore),
            nameof<ControlInfoModel>((x) => x.designScoreColor)
        ),
        RendererConfig.createScoreConfig(
            nameof<ControlInfoModel>((x) => x.implementationScore),
            nameof<ControlInfoModel>((x) => x.implementationScoreColor)
        ),
        RendererConfig.createScoreConfig(
            nameof<ControlInfoModel>((x) => x.effectivenessScore),
            nameof<ControlInfoModel>((x) => x.effectivenessScoreColor)
        ),
        RendererConfig.createScoreConfig(
            nameof<ControlInfoModel>((x) => x.finalConclusionScore),
            nameof<ControlInfoModel>((x) => x.finalConclusionScoreColor)
        ),
        RendererConfig.createScoreConfig(
            nameof<ControlInfoModel>((x) => x.latestPeriodScore),
            nameof<ControlInfoModel>((x) => x.latestPeriodScoreColor)
        ),
        RendererConfig.createShortTimeConfig(nameof<ControlInfoModel>((x) => x.dateCreated)),
        RendererConfig.createShortTimeConfig(nameof<ControlInfoModel>((x) => x.dateModified)),
        RendererConfig.createDateConfig(nameof<ControlInfoModel>((x) => x.dateEffTested)),
        RendererConfig.createOverdueConfig(nameof<ControlInfoModel>((x) => x.nextEffTestDate)),
        RendererConfig.createDateConfig(nameof<ControlInfoModel>((x) => x.dateDiTested)),
        RendererConfig.createOverdueConfig(nameof<ControlInfoModel>((x) => x.nextDiTestDate)),
        RendererConfig.createOverdueConfig(nameof<ControlInfoModel>((x) => x.nextControlExecutionDate)),
        RendererConfig.createDateConfig(nameof<ControlInfoModel>((x) => x.lastControlExecutionDate)),
        RendererConfig.createDateConfig(nameof<ControlInfoModel>((x) => x.startDate)),
        RendererConfig.createDateConfig(nameof<ControlInfoModel>((x) => x.endDate)),
    ];

    buttonConfig: WorkspaceButtonConfig[];

    numberProps: string[] = [
        nameof<ControlInfoModel>((x) => x.riskCount),
    ];
    dateProps: string[] = [
        nameof<ControlInfoModel>((x) => x.dateCreated),
        nameof<ControlInfoModel>((x) => x.dateModified),
        nameof<ControlInfoModel>((x) => x.dateEffTested),
        nameof<ControlInfoModel>((x) => x.nextEffTestDate),
        nameof<ControlInfoModel>((x) => x.dateDiTested),
        nameof<ControlInfoModel>((x) => x.nextDiTestDate),
        nameof<ControlInfoModel>((x) => x.startDate),
        nameof<ControlInfoModel>((x) => x.endDate),
    ];

    permissions: WorkspaceModulePermissionModel;
    
    async ngOnInit(): Promise<void> {
        await this._translation.ready();

        this.permissions = this._permissions.permissions.Workspace.control;

        this.headerLookup = this.buildHeaderLookup();
        this.filterConfigs = this.buildFilterConfigs();
        this.buttonConfig = this.buildButtonConfig();

        this.data = this.getFormattedData();
    }

    private buildFilterConfigs(): FilterConfig[] {
        return [
            {
                label: this._translation.t("overview.filterOrganizations"),
                column: nameof<ControlInfoModel>((x) => x.organizationId),
                type: FilterType.Organization,
                hideActionColumn: true,
                param: "",
            },
            {
                label: this._translation.t("overview.filterBusinessDimensions"),
                column: nameof<ControlInfoModel>((x) => x.businessDimensionIds),
                type: FilterType.BusinessDimensions,
                hideActionColumn: true,
                param: "",
            },
            {
                label: this._translation.t("overview.filterFrameworkDimensions"),
                column: nameof<ControlInfoModel>((x) => x.frameworkDimensionIds),
                type: FilterType.FrameworkDimensions,
                hideActionColumn: true,
                param: "",
            },
        ];
    }

    private buildButtonConfig(): WorkspaceButtonConfig[] {
        return [
            {
                text: this._translation.t("overview.buttonSharedControls"),
                icon: "fa-share-alt",
                getVisibleRange: false,
                clickEvent: () =>
                    this._tabs.generateTab(
                        this.pages.SharedControlOverview,
                        null,
                        this._translation.t("overview.buttonSharedControlsTab")
                    ),
                isDisabled: !this._permissions.permissions.HasSharedControlAccess,
            },
            {
                text: this._translation.t("overview.buttonExportTestPlan"),
                icon: "fa-file-archive",
                getVisibleRange: true,
                clickEvent: (data) =>
                    this._tabs.generateTab(
                        this.pages.ExportEffectiveness,
                        null,
                        this._translation.t("overview.buttonExportTestPlan"),
                        data
                    ),
                isDisabled: !this._permissions.permissions.HasEffectivenessExportAccess,
            },
            {
                text: this._translation.t("overview.buttonCopyTestPlan"),
                icon: "fa-copy",
                getVisibleRange: true,
                clickEvent: (data) =>
                    this._tabs.generateTab(
                        this.pages.CopyEffectiveness,
                        null,
                        this._translation.t("overview.buttonCopyTestPlan"),
                        data
                    ),
                isDisabled: !this._permissions.permissions.HasEffectivenessCopyAccess,
            },
        ];
    }

    async getFormattedData() {
        const data = await firstValueFrom(this._controlDS.getList());

        await this.expandStandingDataCustomFields(data);

        return this.mapHeaders(data);
    }

    private mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private async expandStandingDataCustomFields(data: Object[]) {
        const expandableFields = (
            await firstValueFrom(this.standingDataService
                .getAllByType(StandingDataType.ControlCustomFields))
        ).filter((x) => x.visible);

        CustomFieldHelper.expandCustomFields(
            data,
            "customFields",
            expandableFields,
            this.headerLookup,
            this.rendererConfig
        );
    }

    open(control: ControlInfoModel): void {
        this._tabs.generateTab(
            this.pages.ControlDetail,
            control.guid,
            this._translation.t("overview.openControlTabTitle", {
                identifier: +control.identifier,
                name: control.name,
            })
        );
    }

    add(): void {
        this._tabs.generateTab(
            this.pages.ControlDetail,
            null,
            this._translation.t("overview.addNewControl")
        );
    }
}
